import {
  getPrimaryContactMethod,
  normalizeContactMethods,
  sortContactMethods,
} from '../utils/contactMethods';

export const SETTINGS_BACKUP_VERSION = '1.0.0';

export const EXPORT_DATASETS = [
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'supplierContacts', label: 'Supplier Contacts' },
  { key: 'products', label: 'Products' },
  { key: 'categories', label: 'Categories' },
  { key: 'users', label: 'Users' },
  { key: 'productVariants', label: 'Product Variants' },
  { key: 'priceHistory', label: 'Price History' },
  { key: 'productSuppliers', label: 'Product Supplier Relations' },
  { key: 'productComponents', label: 'Product Components' },
  { key: 'componentSuppliers', label: 'Component Supplier Relations' },
  { key: 'componentPriceHistory', label: 'Component Price History' },
  { key: 'mixedProductInputs', label: 'Mixed Product Inputs' },
  { key: 'mixedProductComponents', label: 'Mixed Product Components' },
  { key: 'bundles', label: 'Configurations' },
  { key: 'bundleComponents', label: 'Configuration Components' },
  { key: 'bundleExtraCosts', label: 'Configuration Extra Costs' },
];

export const RESTORE_COLLECTIONS = [
  'categories',
  'suppliers',
  'supplierContacts',
  'products',
  'productVariants',
  'productSuppliers',
  'priceHistory',
  'productComponents',
  'componentSuppliers',
  'componentPriceHistory',
  'mixedProductInputs',
  'mixedProductComponents',
  'bundles',
  'bundleComponents',
  'bundleExtraCosts',
];

const RESTORE_COLLECTION_ALIASES = {
  productVariants: ['productVariants', 'variants'],
};

export const REQUIRED_BACKUP_COLLECTIONS = RESTORE_COLLECTIONS;
const RESTORE_DOCUMENT_ID_MAX_LENGTH = 1500;

export const getAllExportDatasetKeys = () =>
  EXPORT_DATASETS.map((dataset) => dataset.key);

const normalizeSelectedKeys = (selectedKeys) =>
  Array.from(
    new Set(
      (Array.isArray(selectedKeys) ? selectedKeys : [])
        .filter((key) => getAllExportDatasetKeys().includes(key))
    )
  );

export const areAllExportDatasetsSelected = (selectedKeys) =>
  normalizeSelectedKeys(selectedKeys).length === EXPORT_DATASETS.length;

export const toggleAllExportDatasets = (selectedKeys) =>
  areAllExportDatasetsSelected(selectedKeys) ? [] : getAllExportDatasetKeys();

export const setExportDatasetSelected = (selectedKeys, datasetKey, isSelected) => {
  const currentKeys = normalizeSelectedKeys(selectedKeys);

  if (!getAllExportDatasetKeys().includes(datasetKey)) {
    return currentKeys;
  }

  if (isSelected) {
    return normalizeSelectedKeys([...currentKeys, datasetKey]);
  }

  return currentKeys.filter((key) => key !== datasetKey);
};

const isTimestampLike = (value) =>
  value &&
  typeof value === 'object' &&
  typeof value.seconds === 'number' &&
  typeof value.nanoseconds === 'number';

const toIsoDate = (value) => {
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : isTimestampLike(value)
      ? new Date(value.seconds * 1000 + Math.floor(value.nanoseconds / 1000000))
      : value instanceof Date
        ? value
        : null;

  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
};

export const toPlainExportValue = (value) => {
  const isoDate = toIsoDate(value);

  if (isoDate) {
    return isoDate;
  }

  if (Array.isArray(value)) {
    return value.map((item) => toPlainExportValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((accumulator, [key, itemValue]) => {
      accumulator[key] = toPlainExportValue(itemValue);
      return accumulator;
    }, {});
  }

  return value ?? null;
};

const normalizeCollection = (items) =>
  (Array.isArray(items) ? items : []).map((item) => toPlainExportValue(item));

const normalizeBackupCollection = (key, items) => {
  const rows = normalizeCollection(items);

  if (key === 'suppliers') {
    return rows.map((row) => ({
      ...row,
      contactMethods: normalizeContactMethods(row),
    }));
  }

  if (key === 'supplierContacts') {
    return rows.map((row) => ({
      ...row,
      contactMethods: normalizeContactMethods(row, {
        numberKeys: ['contactNumber', 'phone'],
        whatsappKeys: ['contactWhatsapp', 'whatsapp'],
        defaultLabel: 'Mobile',
      }),
    }));
  }

  return rows;
};

const getBackupCollection = (payload, key) => {
  if (Array.isArray(payload?.[key])) {
    return payload[key];
  }

  const aliasKey = (RESTORE_COLLECTION_ALIASES[key] || []).find((alias) =>
    Array.isArray(payload?.[alias])
  );

  return aliasKey ? payload[aliasKey] : null;
};

const validateRestoreDocumentValue = (value, path, errors, insideArray = false) => {
  if (value === null) {
    return;
  }

  if (Array.isArray(value)) {
    if (insideArray) {
      errors.push(`${path} cannot contain nested arrays.`);
      return;
    }

    value.forEach((item, index) =>
      validateRestoreDocumentValue(item, `${path}[${index}]`, errors, true)
    );
    return;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      errors.push(`${path} must be a finite number.`);
    }
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([fieldName, itemValue]) => {
      if (!fieldName) {
        errors.push(`${path} contains an empty field name.`);
        return;
      }

      validateRestoreDocumentValue(itemValue, `${path}.${fieldName}`, errors);
    });
    return;
  }

  if (!['string', 'boolean'].includes(typeof value)) {
    errors.push(`${path} contains an unsupported value type.`);
  }
};

export const buildExportPayload = (
  dataByCollection,
  selectedKeys,
  { createdAt = new Date().toISOString(), exportedBy = 'Unknown user' } = {}
) => {
  const normalizedKeys = normalizeSelectedKeys(selectedKeys);
  const collections = normalizedKeys.reduce((accumulator, key) => {
    accumulator[key] = normalizeBackupCollection(key, dataByCollection?.[key]);
    return accumulator;
  }, {});

  return {
    version: SETTINGS_BACKUP_VERSION,
    createdAt,
    exportedBy,
    collections,
  };
};

export const buildBackupPayload = (
  dataByCollection,
  { createdAt = new Date().toISOString(), exportedBy = 'Unknown user' } = {}
) => {
  const backup = {
    version: SETTINGS_BACKUP_VERSION,
    createdAt,
    exportedBy,
  };

  REQUIRED_BACKUP_COLLECTIONS.forEach((key) => {
    backup[key] = normalizeBackupCollection(key, dataByCollection?.[key]);
  });

  return backup;
};

export const validateBackupPayload = (payload) => {
  const errors = [];

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      isValid: false,
      errors: ['Backup file must contain a JSON object.'],
      summary: {},
    };
  }

  if (!payload.version || typeof payload.version !== 'string') {
    errors.push('Backup version field is required.');
  }

  REQUIRED_BACKUP_COLLECTIONS.forEach((key) => {
    const collection = getBackupCollection(payload, key);

    if (!Array.isArray(collection)) {
      errors.push(`${key} collection is required and must be an array.`);
      return;
    }

    const ids = new Set();

    collection.forEach((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        errors.push(`${key}[${index}] must be an object.`);
        return;
      }

      const id = typeof item.id === 'string' ? item.id.trim() : '';

      if (!id) {
        errors.push(`${key}[${index}] id is required.`);
        return;
      }

      if (id.includes('/')) {
        errors.push(`${key}[${index}] id cannot contain "/".`);
        return;
      }

      if (id === '.' || id === '..') {
        errors.push(`${key}[${index}] id cannot be "." or "..".`);
        return;
      }

      if (id.length > RESTORE_DOCUMENT_ID_MAX_LENGTH) {
        errors.push(
          `${key}[${index}] id cannot exceed ${RESTORE_DOCUMENT_ID_MAX_LENGTH} characters.`
        );
        return;
      }

      if (ids.has(id)) {
        errors.push(`${key} contains duplicate id "${id}".`);
        return;
      }

      ids.add(id);
      Object.entries(item).forEach(([fieldName, value]) => {
        if (fieldName !== 'id') {
          validateRestoreDocumentValue(value, `${key}[${index}].${fieldName}`, errors);
        }
      });
    });
  });

  const summary = REQUIRED_BACKUP_COLLECTIONS.reduce((accumulator, key) => {
    const collection = getBackupCollection(payload, key);
    accumulator[key] = Array.isArray(collection) ? collection.length : 0;
    return accumulator;
  }, {});

  return {
    isValid: errors.length === 0,
    errors,
    summary,
    version: payload.version || '',
    createdAt: payload.createdAt || '',
    exportedBy: payload.exportedBy || '',
  };
};

export const parseBackupJson = (fileText) => {
  try {
    const payload = JSON.parse(fileText);
    const validation = validateBackupPayload(payload);

    return { payload, validation };
  } catch (error) {
    return {
      payload: null,
      validation: {
        isValid: false,
        errors: ['Backup file must be valid JSON.'],
        summary: {},
      },
    };
  }
};

export const buildRestoreValidation = (payload, role) => {
  const validation = validateBackupPayload(payload);

  if (role !== 'owner') {
    return {
      ...validation,
      isValid: false,
      errors: ['Only owner accounts can restore backups.', ...validation.errors],
    };
  }

  return validation;
};

export const createRestoreExecutionPlan = (payload, role) => {
  const validation = buildRestoreValidation(payload, role);

  return {
    canExecute: validation.isValid,
    validation,
    status: validation.isValid ? 'validated' : 'blocked',
    finalStep:
      'Restore supported Firestore collections through the owner-only restore function with backend preflight and rollback protection.',
  };
};

const formatDateSegment = (date = new Date()) => {
  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }

  return parsedDate.toISOString().slice(0, 10);
};

export const buildBackupFilename = (date = new Date()) =>
  `vacanza-backup-${formatDateSegment(date)}.json`;

export const buildExportFilename = (format, date = new Date()) => {
  const extension = format === 'pdf' ? 'pdf' : 'xlsx';

  return `vacanza-export-${formatDateSegment(date)}.${extension}`;
};

const datasetLabelByKey = EXPORT_DATASETS.reduce((accumulator, dataset) => {
  accumulator[dataset.key] = dataset.label;
  return accumulator;
}, {});

const flattenCellValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const getReadableCellValue = (row, header) => {
  return flattenCellValue(row?.[header]);
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .flatMap((item) => {
            if (typeof item === 'string') {
              return item.split(',');
            }

            if (item && typeof item === 'object') {
              return [item.id || item.key || item.value || ''];
            }

            return [];
          })
          .map((item) => normalizeString(item))
          .filter(Boolean)
      )
    );
  }

  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
};

const hasCollection = (collections, key) =>
  Object.prototype.hasOwnProperty.call(collections || {}, key);

const normalizeExportCollections = (payload) => {
  const sourceCollections = payload?.collections || payload || {};

  return getAllExportDatasetKeys().reduce((accumulator, key) => {
    if (hasCollection(sourceCollections, key)) {
      accumulator[key] = normalizeBackupCollection(key, sourceCollections[key]);
    }

    return accumulator;
  }, {});
};

const createLookup = (rows = [], labelFields = []) =>
  (Array.isArray(rows) ? rows : []).reduce((lookup, row) => {
    const id = normalizeString(row?.id || row?.uid);

    if (!id) {
      return lookup;
    }

    const label = labelFields
      .map((field) => normalizeString(row?.[field]))
      .find(Boolean);

    lookup[id] = label || 'Unnamed record';
    return lookup;
  }, {});

const titleCase = (value) => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return '';
  }

  return normalizedValue
    .split(/[\s_-]+/)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
};

const getCategoryName = (categoryId, categoryLookup) =>
  categoryLookup[categoryId] || '';

const formatNameList = (values = [], fallback = '') => {
  const labels = values.map((value) => normalizeString(value)).filter(Boolean);

  return labels.length > 0 ? labels.join(', ') : fallback;
};

const getCategoryNames = (ids, categoryLookup) =>
  formatNameList(
    normalizeIdList(ids).map((categoryId) => getCategoryName(categoryId, categoryLookup)),
    ''
  );

const getStatusLabel = (row = {}) =>
  row?.isDeleted === true
    ? 'Deleted'
    : row?.isActive === false
      ? 'Inactive'
      : titleCase(row?.status) || 'Active';

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  return parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
};

const formatDateValue = (value) => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return '';
  }

  const isoValue = parsedDate.toISOString();

  return `${isoValue.slice(0, 10)} ${isoValue.slice(11, 16)} UTC`;
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const formatCurrency = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '';
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? `$${numericValue.toFixed(2)}` : '';
};

const formatProductType = (type) =>
  type === 'variant' ? 'Has Variants' : type === 'mixed' ? 'Mixed' : 'Simple';

const getVariantName = (variant = {}) =>
  normalizeString(variant.variantName || variant.name) || 'Standard';

const getProductSupplier = (variant, productSupplierById) =>
  variant?.productSupplierId ? productSupplierById[variant.productSupplierId] : null;

const getVariantSupplierId = (variant, productSupplierById) =>
  normalizeString(variant?.supplierId) ||
  normalizeString(getProductSupplier(variant, productSupplierById)?.supplierId);

const getSupplierName = (supplierId, supplierLookup) =>
  supplierLookup[supplierId] || 'Unknown supplier';

const getProductName = (productId, productLookup) =>
  productLookup[productId] || 'Unknown product';

const buildPricingRows = ({
  productSuppliers,
  productVariants,
  priceHistory,
  productLookup,
  supplierLookup,
  productById,
  productSupplierById,
  variantById,
}) => {
  const pricingRows = [];
  const variantsWithHistory = new Set();

  productSuppliers.forEach((productSupplier) => {
    const productId = normalizeString(productSupplier?.productId);
    const supplierId = normalizeString(productSupplier?.supplierId);
    const history = Array.isArray(productSupplier?.priceHistory)
      ? productSupplier.priceHistory
      : [];

    history.forEach((entry) => {
      pricingRows.push({
        Product: getProductName(productId, productLookup),
        'Product Type': formatProductType(productById[productId]?.type),
        Supplier: getSupplierName(supplierId, supplierLookup),
        Variant: 'Standard',
        Price: formatCurrency(entry?.price),
        Date: formatDateValue(entry?.date),
        Notes: normalizeString(entry?.note || entry?.notes),
        Source: 'Supplier price history',
      });
    });
  });

  priceHistory.forEach((entry) => {
    const variant = variantById[normalizeString(entry?.variantId)] || {};
    const productId = normalizeString(variant?.productId);
    const supplierId = getVariantSupplierId(variant, productSupplierById);

    if (variant?.id) {
      variantsWithHistory.add(variant.id);
    }

    pricingRows.push({
      Product: getProductName(productId, productLookup),
      'Product Type': formatProductType(productById[productId]?.type),
      Supplier: getSupplierName(supplierId, supplierLookup),
      Variant: getVariantName(variant),
      Price: formatCurrency(entry?.price),
      Date: formatDateValue(entry?.date),
      Notes: normalizeString(entry?.note || entry?.notes),
      Source: 'Variant price history',
    });
  });

  productVariants.forEach((variant) => {
    const variantId = normalizeString(variant?.id);

    if (
      variantsWithHistory.has(variantId) ||
      variant?.currentPrice === null ||
      variant?.currentPrice === undefined ||
      String(variant.currentPrice).trim() === ''
    ) {
      return;
    }

    const productId = normalizeString(variant?.productId);
    const supplierId = getVariantSupplierId(variant, productSupplierById);

    pricingRows.push({
      Product: getProductName(productId, productLookup),
      'Product Type': formatProductType(productById[productId]?.type),
      Supplier: getSupplierName(supplierId, supplierLookup),
      Variant: getVariantName(variant),
      Price: formatCurrency(variant.currentPrice),
      Date: formatDateValue(variant.lastUpdated),
      Notes: normalizeString(variant.notes),
      Source: 'Current variant price',
    });
  });

  return pricingRows.sort((first, second) =>
    [
      first.Product.localeCompare(second.Product),
      first.Supplier.localeCompare(second.Supplier),
      first.Variant.localeCompare(second.Variant),
      second.Date.localeCompare(first.Date),
    ].find((result) => result !== 0) || 0
  );
};

const getLatestPricingByProduct = (pricingRows) =>
  pricingRows.reduce((lookup, row) => {
    const productName = normalizeString(row.Product);

    if (!productName || !row.Price) {
      return lookup;
    }

    const current = lookup[productName];

    if (!current || normalizeString(row.Date) > normalizeString(current.Date)) {
      lookup[productName] = row;
    }

    return lookup;
  }, {});

const createSheet = (key, label, headers, rows) => ({
  key,
  label,
  headers,
  rows: rows.map((row) =>
    headers.reduce((normalizedRow, header) => {
      normalizedRow[header] = row?.[header] ?? '';
      return normalizedRow;
    }, {})
  ),
});

export const buildBusinessExportWorkbook = (payload) => {
  const collections = normalizeExportCollections(payload);
  const suppliers = collections.suppliers || [];
  const supplierContacts = collections.supplierContacts || [];
  const products = collections.products || [];
  const categories = collections.categories || [];
  const users = collections.users || [];
  const productVariants = collections.productVariants || [];
  const productSuppliers = collections.productSuppliers || [];
  const priceHistory = collections.priceHistory || [];
  const supplierLookup = createLookup(suppliers, ['companyName', 'name']);
  const productLookup = createLookup(products, ['name', 'productName']);
  const categoryLookup = createLookup(categories, ['name', 'categoryName']);
  const productById = products.reduce((lookup, product) => {
    lookup[normalizeString(product?.id)] = product;
    return lookup;
  }, {});
  const productSupplierById = productSuppliers.reduce((lookup, productSupplier) => {
    lookup[normalizeString(productSupplier?.id)] = productSupplier;
    return lookup;
  }, {});
  const variantById = productVariants.reduce((lookup, variant) => {
    lookup[normalizeString(variant?.id)] = variant;
    return lookup;
  }, {});
  const pricingRows = buildPricingRows({
    productSuppliers,
    productVariants,
    priceHistory,
    productLookup,
    supplierLookup,
    productById,
    productSupplierById,
    variantById,
  });
  const latestPricingByProduct = getLatestPricingByProduct(pricingRows);
  const supplierMethodRows = [];

  suppliers.forEach((supplier) => {
    sortContactMethods(normalizeContactMethods(supplier)).forEach((method) => {
      supplierMethodRows.push({
        'Owner Type': 'Supplier',
        Supplier: supplierLookup[supplier.id] || 'Unnamed supplier',
        Contact: '',
        Label: normalizeString(method.label) || 'Other',
        Number: normalizeString(method.number),
        WhatsApp: formatBoolean(method.hasWhatsapp),
        Primary: formatBoolean(method.isPrimary),
      });
    });
  });

  supplierContacts.forEach((contact) => {
    sortContactMethods(normalizeContactMethods(contact, {
      numberKeys: ['contactNumber', 'phone'],
      whatsappKeys: ['contactWhatsapp', 'whatsapp'],
      defaultLabel: 'Mobile',
    })).forEach((method) => {
      supplierMethodRows.push({
        'Owner Type': 'Supplier Contact',
        Supplier: getSupplierName(contact.supplierId, supplierLookup),
        Contact: normalizeString(contact.name) || 'Unnamed contact',
        Label: normalizeString(method.label) || 'Other',
        Number: normalizeString(method.number),
        WhatsApp: formatBoolean(method.hasWhatsapp),
        Primary: formatBoolean(method.isPrimary),
      });
    });
  });

  const productSupplierCounts = productSuppliers.reduce((lookup, productSupplier) => {
    const productId = normalizeString(productSupplier?.productId);

    if (productId) {
      lookup[productId] = lookup[productId] || new Set();
      lookup[productId].add(normalizeString(productSupplier?.supplierId));
    }

    return lookup;
  }, {});

  productVariants.forEach((variant) => {
    const productId = normalizeString(variant?.productId);
    const supplierId = getVariantSupplierId(variant, productSupplierById);

    if (productId && supplierId) {
      productSupplierCounts[productId] = productSupplierCounts[productId] || new Set();
      productSupplierCounts[productId].add(supplierId);
    }
  });

  const productVariantCounts = productVariants.reduce((lookup, variant) => {
    const productId = normalizeString(variant?.productId);

    if (productId && variant?.isActive !== false) {
      lookup[productId] = (lookup[productId] || 0) + 1;
    }

    return lookup;
  }, {});

  const summaryRows = [
    { Metric: 'Export Created At', Value: formatDateValue(payload?.createdAt || new Date()) },
    { Metric: 'Exported By', Value: payload?.exportedBy || 'Unknown user' },
    { Metric: 'Suppliers', Value: suppliers.length },
    { Metric: 'Active Suppliers', Value: suppliers.filter((supplier) => supplier?.isDeleted !== true).length },
    { Metric: 'Supplier Contacts', Value: supplierContacts.length },
    { Metric: 'Supplier Contact Methods', Value: supplierMethodRows.length },
    { Metric: 'Products', Value: products.length },
    { Metric: 'Active Products', Value: products.filter((product) => product?.isDeleted !== true).length },
    { Metric: 'Product Pricing Records', Value: pricingRows.length },
    { Metric: 'Categories', Value: categories.length },
    { Metric: 'Users Included', Value: hasCollection(collections, 'users') ? users.length : 'No' },
  ];

  const supplierRows = suppliers.map((supplier) => {
    const primaryMethod = getPrimaryContactMethod(normalizeContactMethods(supplier));
    const socials = supplier?.socials && typeof supplier.socials === 'object'
      ? supplier.socials
      : {};

    return {
      'Supplier Name': supplierLookup[supplier.id] || 'Unnamed supplier',
      Status: getStatusLabel(supplier),
      Categories: getCategoryNames(supplier.categories, categoryLookup),
      'Primary Phone': primaryMethod?.number || normalizeString(supplier.companyPhone || supplier.phone),
      'Primary WhatsApp': primaryMethod?.hasWhatsapp ? primaryMethod.number : '',
      Email: normalizeString(supplier.companyEmail || supplier.email),
      Website: normalizeString(supplier.website),
      Address: normalizeString(supplier.address),
      'Maps Link': normalizeString(supplier.mapsLink),
      Facebook: normalizeString(socials.facebook || supplier.facebook),
      Instagram: normalizeString(socials.instagram || supplier.instagram),
      TikTok: normalizeString(socials.tiktok || supplier.tiktok),
      LinkedIn: normalizeString(socials.linkedin || supplier.linkedin),
      Telegram: normalizeString(socials.telegram || supplier.telegram),
      Notes: normalizeString(supplier.comments || supplier.notes),
      'Created At': formatDateValue(supplier.createdAt),
    };
  });

  const contactRows = supplierContacts.map((contact) => {
    const primaryMethod = getPrimaryContactMethod(normalizeContactMethods(contact, {
      numberKeys: ['contactNumber', 'phone'],
      whatsappKeys: ['contactWhatsapp', 'whatsapp'],
      defaultLabel: 'Mobile',
    }));

    return {
      Supplier: getSupplierName(contact.supplierId, supplierLookup),
      'Contact Name': normalizeString(contact.name) || 'Unnamed contact',
      Role: normalizeString(contact.role),
      Email: normalizeString(contact.email),
      'Primary Phone': primaryMethod?.number || normalizeString(contact.phone),
      'Primary WhatsApp': primaryMethod?.hasWhatsapp
        ? primaryMethod.number
        : normalizeString(contact.whatsapp),
      Primary: formatBoolean(contact.isPrimary),
      'Created At': formatDateValue(contact.createdAt),
    };
  });

  const productRows = products.map((product) => {
    const productId = normalizeString(product.id);
    const latestPricing = latestPricingByProduct[productLookup[productId]];

    return {
      'Product Name': productLookup[productId] || 'Unnamed product',
      Type: formatProductType(product.type),
      Categories: getCategoryNames(product.categories, categoryLookup),
      'Allowed Variants': formatNameList(normalizeIdList(product.allowedVariants)),
      'Linked Suppliers': productSupplierCounts[productId]?.size || 0,
      'Active Variants': productVariantCounts[productId] || 0,
      'Latest Price': latestPricing?.Price || '',
      'Latest Price Supplier': latestPricing?.Supplier || '',
      Status: getStatusLabel(product),
      Description: normalizeString(product.description),
      'Created At': formatDateValue(product.createdAt),
    };
  });

  const categoryRows = categories.map((category) => {
    const categoryId = normalizeString(category.id);
    const productCount = products.filter((product) =>
      normalizeIdList(product.categories).includes(categoryId)
    ).length;
    const supplierCount = suppliers.filter((supplier) =>
      normalizeIdList(supplier.categories).includes(categoryId)
    ).length;

    return {
      'Category Name': categoryLookup[categoryId] || 'Unnamed category',
      Status: getStatusLabel(category),
      Products: productCount,
      Suppliers: supplierCount,
      Slug: normalizeString(category.slug),
      'Created At': formatDateValue(category.createdAt),
    };
  });

  const userRows = users.map((user) => ({
    Name: normalizeString(user.name) || normalizeString(user.email) || 'Unnamed user',
    Email: normalizeString(user.email),
    Role: titleCase(user.role),
    Status: getStatusLabel(user),
    Active: formatBoolean(user.isActive !== false),
    'Created At': formatDateValue(user.createdAt),
    'Updated At': formatDateValue(user.updatedAt),
  }));

  const includedDatasets = Object.keys(collections)
    .map((key) => datasetLabelByKey[key] || key)
    .join(', ');
  const metadataRows = [
    { Field: 'Export Version', Value: payload?.version || SETTINGS_BACKUP_VERSION },
    { Field: 'Created At', Value: formatDateValue(payload?.createdAt || new Date()) },
    { Field: 'Exported By', Value: payload?.exportedBy || 'Unknown user' },
    { Field: 'Included Datasets', Value: includedDatasets || 'None' },
    { Field: 'Backup Compatibility', Value: 'Presentation export only; JSON backup and restore format unchanged.' },
    ...Object.keys(collections).map((key) => ({
      Field: `${datasetLabelByKey[key] || key} Records`,
      Value: Array.isArray(collections[key]) ? collections[key].length : 0,
    })),
  ];

  const sheets = [
    createSheet('summary', 'Summary', ['Metric', 'Value'], summaryRows),
    createSheet('suppliers', 'Suppliers', [
      'Supplier Name',
      'Status',
      'Categories',
      'Primary Phone',
      'Primary WhatsApp',
      'Email',
      'Website',
      'Address',
      'Maps Link',
      'Facebook',
      'Instagram',
      'TikTok',
      'LinkedIn',
      'Telegram',
      'Notes',
      'Created At',
    ], supplierRows),
    createSheet('supplierContactMethods', 'Supplier Contact Methods', [
      'Owner Type',
      'Supplier',
      'Contact',
      'Label',
      'Number',
      'WhatsApp',
      'Primary',
    ], supplierMethodRows),
    createSheet('supplierContacts', 'Supplier Contacts', [
      'Supplier',
      'Contact Name',
      'Role',
      'Email',
      'Primary Phone',
      'Primary WhatsApp',
      'Primary',
      'Created At',
    ], contactRows),
    createSheet('products', 'Products', [
      'Product Name',
      'Type',
      'Categories',
      'Allowed Variants',
      'Linked Suppliers',
      'Active Variants',
      'Latest Price',
      'Latest Price Supplier',
      'Status',
      'Description',
      'Created At',
    ], productRows),
    createSheet('productPricing', 'Product Pricing', [
      'Product',
      'Product Type',
      'Supplier',
      'Variant',
      'Price',
      'Date',
      'Notes',
      'Source',
    ], pricingRows),
    createSheet('categories', 'Categories', [
      'Category Name',
      'Status',
      'Products',
      'Suppliers',
      'Slug',
      'Created At',
    ], categoryRows),
    ...(hasCollection(collections, 'users')
      ? [createSheet('users', 'Users', [
        'Name',
        'Email',
        'Role',
        'Status',
        'Active',
        'Created At',
        'Updated At',
      ], userRows)]
      : []),
    createSheet('metadata', 'Metadata', ['Field', 'Value'], metadataRows),
  ];

  return {
    sheets,
    report: {
      summaryRows,
      supplierRows,
      contactRows,
      productRows,
      pricingRows,
      categoryRows,
      userRows,
      metadataRows,
      usersIncluded: hasCollection(collections, 'users'),
    },
  };
};

export const getTabularCollections = (payload) =>
  buildBusinessExportWorkbook(payload).sheets;

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const getColumnName = (index) => {
  let column = '';
  let currentIndex = index + 1;

  while (currentIndex > 0) {
    const remainder = (currentIndex - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    currentIndex = Math.floor((currentIndex - 1) / 26);
  }

  return column;
};

const getColumnWidth = (rows, header) => {
  const values = [
    header,
    ...rows.map((row) => getReadableCellValue(row, header)),
  ];
  const longestValue = values.reduce((longest, value) => {
    const valueLength = String(value || '')
      .split('\n')
      .reduce((maxLength, line) => Math.max(maxLength, line.length), 0);

    return Math.max(longest, valueLength);
  }, 0);

  return Math.max(10, Math.min(50, longestValue + 2));
};

const buildSheetXml = ({ headers, rows }) => {
  const allRows = [
    headers,
    ...rows.map((row) => headers.map((header) => getReadableCellValue(row, header))),
  ];
  const columnDefinitions = headers.map((header, index) => {
    const columnIndex = index + 1;
    const width = getColumnWidth(rows, header);

    return `<col min="${columnIndex}" max="${columnIndex}" width="${width}" customWidth="1"/>`;
  });

  const sheetRows = allRows.map((row, rowIndex) => {
    const cells = row.map((cell, cellIndex) => {
      const cellRef = `${getColumnName(cellIndex)}${rowIndex + 1}`;
      const styleAttribute = rowIndex === 0 ? ' s="1"' : '';

      return `<c r="${cellRef}"${styleAttribute} t="inlineStr"><is><t>${escapeXml(cell)}</t></is></c>`;
    });

    return `<row r="${rowIndex + 1}">${cells.join('')}</row>`;
  });

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${columnDefinitions.join('')}</cols><sheetData>${sheetRows.join('')}</sheetData></worksheet>`;
};

const crcTable = Array.from({ length: 256 }, (_, tableIndex) => {
  let crc = tableIndex;

  for (let bit = 0; bit < 8; bit += 1) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }

  return crc >>> 0;
});

const getCrc32 = (bytes) => {
  let crc = 0xffffffff;

  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (buffer, offset, value) => {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
};

const writeUint32 = (buffer, offset, value) => {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
  buffer[offset + 2] = (value >>> 16) & 0xff;
  buffer[offset + 3] = (value >>> 24) & 0xff;
};

const concatBytes = (parts) => {
  const totalLength = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;

  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });

  return output;
};

const createZipBlob = (entries, mimeType) => {
  const encoder = new TextEncoder();
  const fileParts = [];
  const centralParts = [];
  let offset = 0;

  entries.forEach((entry) => {
    const nameBytes = encoder.encode(entry.name);
    const dataBytes = typeof entry.content === 'string'
      ? encoder.encode(entry.content)
      : entry.content;
    const crc = getCrc32(dataBytes);
    const localHeader = new Uint8Array(30 + nameBytes.length);

    writeUint32(localHeader, 0, 0x04034b50);
    writeUint16(localHeader, 4, 20);
    writeUint16(localHeader, 6, 0);
    writeUint16(localHeader, 8, 0);
    writeUint16(localHeader, 10, 0);
    writeUint16(localHeader, 12, 0);
    writeUint32(localHeader, 14, crc);
    writeUint32(localHeader, 18, dataBytes.length);
    writeUint32(localHeader, 22, dataBytes.length);
    writeUint16(localHeader, 26, nameBytes.length);
    writeUint16(localHeader, 28, 0);
    localHeader.set(nameBytes, 30);
    fileParts.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);

    writeUint32(centralHeader, 0, 0x02014b50);
    writeUint16(centralHeader, 4, 20);
    writeUint16(centralHeader, 6, 20);
    writeUint16(centralHeader, 8, 0);
    writeUint16(centralHeader, 10, 0);
    writeUint16(centralHeader, 12, 0);
    writeUint16(centralHeader, 14, 0);
    writeUint32(centralHeader, 16, crc);
    writeUint32(centralHeader, 20, dataBytes.length);
    writeUint32(centralHeader, 24, dataBytes.length);
    writeUint16(centralHeader, 28, nameBytes.length);
    writeUint16(centralHeader, 30, 0);
    writeUint16(centralHeader, 32, 0);
    writeUint16(centralHeader, 34, 0);
    writeUint16(centralHeader, 36, 0);
    writeUint32(centralHeader, 38, 0);
    writeUint32(centralHeader, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralParts.push(centralHeader);

    offset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatBytes(centralParts);
  const centralOffset = offset;
  const endRecord = new Uint8Array(22);

  writeUint32(endRecord, 0, 0x06054b50);
  writeUint16(endRecord, 4, 0);
  writeUint16(endRecord, 6, 0);
  writeUint16(endRecord, 8, entries.length);
  writeUint16(endRecord, 10, entries.length);
  writeUint32(endRecord, 12, centralDirectory.length);
  writeUint32(endRecord, 16, centralOffset);
  writeUint16(endRecord, 20, 0);

  return new Blob([...fileParts, centralDirectory, endRecord], { type: mimeType });
};

const EXCEL_STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="2">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '</fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '<dxfs count="0"/>' +
  '<tableStyles count="0" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleLight16"/>' +
  '</styleSheet>';

export const createExcelBlob = (payload) => {
  const tabularCollections = getTabularCollections(payload);
  const sheets = tabularCollections.length > 0
    ? tabularCollections
    : [{ key: 'empty', label: 'No Data', headers: ['Message'], rows: [{ Message: 'No data selected' }] }];
  const contentTypeOverrides = sheets.map((sheet, index) =>
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  );
  const workbookSheets = sheets.map((sheet, index) =>
    `<sheet name="${escapeXml(sheet.label).slice(0, 31)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
  );
  const workbookRels = sheets.map((sheet, index) =>
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
  );
  const stylesRelationshipId = `rId${sheets.length + 1}`;
  const entries = [
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>${contentTypeOverrides.join('')}</Types>`,
    },
    {
      name: '_rels/.rels',
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>',
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${workbookSheets.join('')}</sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${workbookRels.join('')}<Relationship Id="${stylesRelationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    {
      name: 'xl/styles.xml',
      content: EXCEL_STYLES_XML,
    },
    {
      name: 'docProps/core.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>Vacanza Export</dc:title><dc:creator>${escapeXml(payload?.exportedBy || 'Vacanza OS')}</dc:creator><dcterms:created xsi:type="dcterms:W3CDTF">${escapeXml(payload?.createdAt || new Date().toISOString())}</dcterms:created></cp:coreProperties>`,
    },
    {
      name: 'docProps/app.xml',
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Vacanza OS</Application></Properties>',
    },
    ...sheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      content: buildSheetXml(sheet),
    })),
  ];

  return createZipBlob(
    entries,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

const escapePdfText = (value) =>
  String(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

const splitLine = (line, maxLength = 95) => {
  if (line.length <= maxLength) {
    return [line];
  }

  const chunks = [];

  for (let index = 0; index < line.length; index += maxLength) {
    chunks.push(line.slice(index, index + maxLength));
  }

  return chunks;
};

const truncatePdfCell = (value, width) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();

  if (text.length <= width) {
    return text.padEnd(width, ' ');
  }

  return `${text.slice(0, Math.max(0, width - 3))}...`;
};

const appendPdfTable = (lines, headers, rows, widths, maxRows = 24) => {
  const headerLine = headers
    .map((header, index) => truncatePdfCell(header, widths[index]))
    .join(' | ');
  const dividerLine = widths.map((width) => '-'.repeat(width)).join('-+-');

  lines.push(headerLine, dividerLine);

  rows.slice(0, maxRows).forEach((row) => {
    lines.push(
      headers
        .map((header, index) => truncatePdfCell(row?.[header], widths[index]))
        .join(' | ')
    );
  });

  if (rows.length > maxRows) {
    lines.push(`Showing first ${maxRows} of ${rows.length} records.`);
  }
};

const appendPdfSection = (lines, title) => {
  if (lines.length > 0) {
    lines.push('');
  }

  lines.push(title);
  lines.push('='.repeat(title.length));
};

const buildManagementReportLines = (payload) => {
  const workbook = buildBusinessExportWorkbook(payload);
  const {
    summaryRows,
    supplierRows,
    productRows,
    categoryRows,
    userRows,
    usersIncluded,
  } = workbook.report;
  const lines = [];

  appendPdfSection(lines, 'Report Header');
  lines.push('Vacanza OS Management Export Report');
  lines.push(`Created At: ${formatDateValue(payload?.createdAt || new Date())}`);
  lines.push(`Exported By: ${payload?.exportedBy || 'Unknown user'}`);
  lines.push(`Version: ${payload?.version || SETTINGS_BACKUP_VERSION}`);

  appendPdfSection(lines, 'Summary Statistics');
  appendPdfTable(lines, ['Metric', 'Value'], summaryRows, [34, 48], 30);

  appendPdfSection(lines, 'Suppliers Overview');
  if (supplierRows.length > 0) {
    appendPdfTable(
      lines,
      ['Supplier Name', 'Status', 'Categories', 'Primary Phone', 'Email'],
      supplierRows,
      [22, 10, 20, 16, 24],
      24
    );
  } else {
    lines.push('No suppliers were included in this export.');
  }

  appendPdfSection(lines, 'Products Overview');
  if (productRows.length > 0) {
    appendPdfTable(
      lines,
      ['Product Name', 'Type', 'Categories', 'Linked Suppliers', 'Latest Price'],
      productRows,
      [24, 13, 22, 16, 12],
      24
    );
  } else {
    lines.push('No products were included in this export.');
  }

  appendPdfSection(lines, 'Categories Overview');
  if (categoryRows.length > 0) {
    appendPdfTable(
      lines,
      ['Category Name', 'Status', 'Products', 'Suppliers'],
      categoryRows,
      [30, 12, 12, 12],
      30
    );
  } else {
    lines.push('No categories were included in this export.');
  }

  appendPdfSection(lines, 'Users Overview');
  if (!usersIncluded) {
    lines.push('Users were not selected for this export.');
  } else if (userRows.length > 0) {
    appendPdfTable(
      lines,
      ['Name', 'Email', 'Role', 'Status'],
      userRows,
      [20, 28, 12, 12],
      24
    );
  } else {
    lines.push('Users were selected, but no user records were available.');
  }

  return lines.flatMap((line) => splitLine(line));
};

export const createPdfBlob = (payload) => {
  const lines = buildManagementReportLines(payload);

  const linesPerPage = 45;
  const pages = [];

  for (let index = 0; index < lines.length; index += linesPerPage) {
    pages.push(lines.slice(index, index + linesPerPage));
  }

  const objects = [];
  const addObject = (content) => {
    objects.push(content);
    return objects.length;
  };
  const fontObjectId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pageObjectIds = [];

  pages.forEach((pageLines) => {
    const textCommands = pageLines
      .map((line, index) => `1 0 0 1 50 ${760 - index * 16} Tm (${escapePdfText(line)}) Tj`)
      .join('\n');
    const stream = `BT /F1 10 Tf ${textCommands} ET`;
    const contentObjectId = addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const pageObjectId = addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>`);

    pageObjectIds.push(pageObjectId);
  });

  const pagesObjectId = addObject(`<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageObjectIds.length} >>`);

  pageObjectIds.forEach((pageObjectId) => {
    objects[pageObjectId - 1] = objects[pageObjectId - 1].replace('/Parent 0 0 R', `/Parent ${pagesObjectId} 0 R`);
  });

  const catalogObjectId = addObject(`<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`);
  let pdf = '%PDF-1.4\n';
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogObjectId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
